import {
    Accordion,
    AccordionPanel,
    Anchor,
    Box,
    Button,
    Heading,
    Layer,
    Markdown,
    Text
} from 'grommet';
import { Alert, Close } from 'grommet-icons';
import { useTranslation } from 'react-i18next';
import { useLocalstorageState } from 'rooks';
import { useState } from 'react';
import { ChangelogTranslation } from '../md-translation/ChangelogTranslation';

const supportedLanguages = ['uk', 'en'];

const markdownComponents = {
    p: { props: { style: { maxWidth: '100%' } } },
    ul: { props: { style: { marginBottom: 0, paddingLeft: '1.25rem' } } }
};

const getSupportedLanguage = (language) => language?.startsWith('uk') ? 'uk' : 'en';

const countListItems = (markdown = '') => markdown.match(/^\s*-\s+/gm)?.length ?? 0;

const sectionLabel = (label, markdown) => `${label} · ${countListItems(markdown)}`;

export const Changelog = () => {
    const { i18n } = useTranslation();
    const [, setLastSeenVersion] = useLocalstorageState('lastSeenVersion');
    const [languageOverride, setLanguageOverride] = useState();
    const [activeSections, setActiveSections] = useState([]);

    const interfaceLanguage = getSupportedLanguage(i18n.resolvedLanguage || i18n.language);
    const language = languageOverride || interfaceLanguage;
    const copy = ChangelogTranslation[language];

    const dismiss = () => setLastSeenVersion(process.env.REACT_APP_VERSION);

    return (
        <Layer animation={false} background="#222222" margin="small" onEsc={dismiss}>
            <Box
                width="large"
                height={{ max: '90vh' }}
            >
                <Box
                    align="start"
                    direction="row"
                    flex={false}
                    gap="small"
                    justify="between"
                    pad={{ top: 'medium', horizontal: 'medium', bottom: 'small' }}
                >
                    <Box gap="xsmall">
                        <Text color="brand" size="small" weight="bold">
                            {copy.updateLabel} {process.env.REACT_APP_VERSION}
                        </Text>
                        <Heading level={2} margin="none" size="small">
                            {copy.title}
                        </Heading>
                    </Box>
                    <Button
                        a11yTitle={copy.closeLabel}
                        icon={<Close />}
                        onClick={dismiss}
                        plain
                    />
                </Box>

                <Box flex overflow="auto" pad={{ horizontal: 'medium', bottom: 'medium' }}>
                    <Box flex={false} gap="medium">
                        <Text color="text-weak">{copy.summary}</Text>

                        <Box
                            aria-label={copy.languageSwitcherLabel}
                            direction="row"
                            gap="xsmall"
                            role="group"
                            wrap
                        >
                            {supportedLanguages.map((languageCode) => (
                                <Button
                                    aria-pressed={language === languageCode}
                                    key={languageCode}
                                    label={ChangelogTranslation[languageCode].languageLabel}
                                    onClick={() => setLanguageOverride(languageCode)}
                                    primary={language === languageCode}
                                    secondary={language !== languageCode}
                                    size="small"
                                />
                            ))}
                        </Box>

                        {copy.importantNotes && (
                            <Box
                                align="start"
                                background="status-critical"
                                direction="row"
                                flex={false}
                                gap="small"
                                pad="small"
                                round="xsmall"
                            >
                                <Box flex={false} pad={{ top: 'xxsmall' }}>
                                    <Alert color="white" />
                                </Box>
                                <Box flex gap="xsmall">
                                    <Text color="white" weight="bold">
                                        {copy.importantNotesLabel}
                                    </Text>
                                    <Text color="white">
                                        {copy.importantNotes}
                                    </Text>
                                </Box>
                            </Box>
                        )}

                        <Box
                            background="background-contrast"
                            flex={false}
                            gap="small"
                            pad="medium"
                            round="small"
                        >
                            <Text color="brand" size="small" weight="bold">
                                {copy.newFeaturesLabel}
                            </Text>
                            <Heading level={3} margin="none" size="small">
                                {copy.newFeatureTitle}
                            </Heading>
                            <Text>{copy.newFeatureSummary}</Text>
                            <Markdown components={markdownComponents}>
                                {copy.newFeatures}
                            </Markdown>
                            <Box
                                gap="small"
                                margin={{ top: 'small' }}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))'
                                }}
                            >
                                {ChangelogTranslation.previews.map((preview) => (
                                    <Box flex={false} gap="xsmall" key={preview.id}>
                                        <Text weight="bold">
                                            {copy.previews[preview.id].title}
                                        </Text>
                                        <Box
                                            background="#151515"
                                            overflow="hidden"
                                            round="medium"
                                        >
                                            <img
                                                alt={copy.previews[preview.id].alt}
                                                loading="lazy"
                                                src={preview.image}
                                                style={{ aspectRatio: '1', borderRadius: '12px', display: 'block', objectFit: 'contain', width: '100%' }}
                                            />
                                        </Box>
                                        <Anchor
                                            a11yTitle={`${copy.previewLinkLabel}: ${copy.previews[preview.id].title}`}
                                            alignSelf="start"
                                            href={preview.image}
                                            label={copy.previewLinkLabel}
                                            rel="noreferrer"
                                            target="_blank"
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {(copy.improvements || copy.bugFixes) && (
                            <Accordion
                                activeIndex={activeSections}
                                multiple
                                onActive={setActiveSections}
                            >
                                {copy.improvements && (
                                    <AccordionPanel label={sectionLabel(copy.improvementsLabel, copy.improvements)}>
                                        <Box
                                            background="background-contrast"
                                            pad={{ horizontal: 'medium', bottom: 'medium' }}
                                        >
                                            <Markdown components={markdownComponents}>
                                                {copy.improvements}
                                            </Markdown>
                                        </Box>
                                    </AccordionPanel>
                                )}
                                {copy.bugFixes && (
                                    <AccordionPanel label={sectionLabel(copy.bugFixesLabel, copy.bugFixes)}>
                                        <Box
                                            background="background-contrast"
                                            pad={{ horizontal: 'medium', bottom: 'medium' }}
                                        >
                                            <Markdown components={markdownComponents}>
                                                {copy.bugFixes}
                                            </Markdown>
                                        </Box>
                                    </AccordionPanel>
                                )}
                            </Accordion>
                        )}
                    </Box>
                </Box>

                <Box flex={false} pad="medium">
                    <Button fill label={copy.confirmLabel} onClick={dismiss} primary />
                </Box>
            </Box>
        </Layer>
    );
};
